/**
 * User Management Utilities
 *
 * Functions for managing user data.
 * All user document writes go through API (users-admin-api.ts).
 */

import { UserRole } from '../types/rbac';
import logger from '@/lib/logging';
import { updateUserProfileApi } from '@/lib/users-admin-api';

export interface EnhancedUser {
  userId: string;
  email: string;
  displayName: string;
  photoURL: string;
  isGuest: boolean;
  roles: UserRole[];
  primaryRole: UserRole | null;
  accessStatus: 'ACTIVE' | 'INACTIVE' | 'BANNED' | null;
  banned: boolean;
  bannedAt?: Date;
  deleted: boolean;
  deletedAt?: Date;
  lastLoginAt?: Date;
  createdAt?: Date;
  isPreAssigned?: boolean;
  /** Row sourced from accessGrants only (not legacy pre_ / users/{uid}/roles). */
  fromAccessGrant?: boolean;
  /** True when this row still has leftover users/{uid}/roles or pre_ role docs. */
  legacyRoleHit?: boolean;
  /** Per-role Access grant source when the row is backed by accessGrants. */
  grantSources?: Partial<Record<UserRole, 'admin' | 'subscription' | 'seed'>>;
}

/**
 * Update a user's display name (via API)
 */
export async function updateDisplayName(userId: string, newDisplayName: string): Promise<boolean> {
  try {
    const success = await updateUserProfileApi(userId, { displayName: newDisplayName });
    if (success) {
      logger.debug(`Updated display name for user ${userId} to ${newDisplayName}`);
    }
    return success;
  } catch (error) {
    logger.error(`Error updating display name for user ${userId}:`, error);
    return false;
  }
}

/**
 * Update a user's email (via API)
 */
export async function updateUserEmail(userId: string, newEmail: string): Promise<boolean> {
  try {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      throw new Error('Invalid email format');
    }
    const success = await updateUserProfileApi(userId, { email: newEmail });
    if (success) {
      logger.debug(`Updated email for user ${userId} to ${newEmail}`);
    }
    return success;
  } catch (error) {
    logger.error(`Error updating email for user ${userId}:`, error);
    return false;
  }
}
